import type {
    ShippingCarrierCreateInput,
    ShippingCarrierUpdateInput,
    ShippingRateCreateInput,
    ShippingRateUpdateInput,
    ShippingSettingsInput,
    ShippingZoneCreateInput,
    ShippingZoneUpdateInput,
} from "@/modules/shipping/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { shippingApi } from "./client";

export const shippingKeys = {
  all: ["shipping"] as const,
  zones: {
    all: ["shipping", "zones"] as const,
    list: () => ["shipping", "zones", "list"] as const,
    detail: (id: string) => ["shipping", "zones", "detail", id] as const,
  },
  rates: {
    all: ["shipping", "rates"] as const,
    list: (q: Partial<OffsetPageQueryDTO>) =>
      ["shipping", "rates", "list", q] as const,
    detail: (id: string) => ["shipping", "rates", "detail", id] as const,
  },
  settings: ["shipping", "settings"] as const,
  carriers: {
    all: ["shipping", "carriers"] as const,
    list: () => ["shipping", "carriers", "list"] as const,
    detail: (id: string) => ["shipping", "carriers", "detail", id] as const,
  },
};

export function useCarriersQuery() {
  return useQuery({
    queryKey: shippingKeys.carriers.list(),
    queryFn: () => shippingApi.carriers.list(),
  });
}

export function useCarrierQuery(id: string) {
  return useQuery({
    queryKey: shippingKeys.carriers.detail(id),
    queryFn: () => shippingApi.carriers.get(id),
    enabled: !!id,
  });
}

export function useCreateCarrier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingCarrierCreateInput) =>
      shippingApi.carriers.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.carriers.all });
      toast.success("ສ້າງຜູ້ໃຫ້ບໍລິການຂົນສົ່ງສຳເລັດ");
    },
  });
}

export function useUpdateCarrier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingCarrierUpdateInput) =>
      shippingApi.carriers.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.carriers.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.carriers.all });
      toast.success("ແກ້ໄຂຜູ້ໃຫ້ບໍລິການຂົນສົ່ງສຳເລັດ");
    },
  });
}

export function useDeleteCarrier() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (carrierId: string) => shippingApi.carriers.remove(carrierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.carriers.all });
    },
  });
  const run = (carrierId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(carrierId, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useZonesQuery() {
  return useQuery({
    queryKey: shippingKeys.zones.list(),
    queryFn: () => shippingApi.zones.list(),
  });
}

export function useZoneQuery(id: string) {
  return useQuery({
    queryKey: shippingKeys.zones.detail(id),
    queryFn: () => shippingApi.zones.get(id),
    enabled: !!id,
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingZoneCreateInput) =>
      shippingApi.zones.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.zones.all });
      toast.success("ສ້າງໂຊນສຳເລັດ");
    },
  });
}

export function useUpdateZone(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingZoneUpdateInput) =>
      shippingApi.zones.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.zones.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.zones.all });
      toast.success("ແກ້ໄຂໂຊນສຳເລັດ");
    },
  });
}

export function useDeleteZone() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => shippingApi.zones.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.zones.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useRatesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: shippingKeys.rates.list(q),
    queryFn: () => shippingApi.rates.list(q),
  });
}

export function useRateQuery(id: string) {
  return useQuery({
    queryKey: shippingKeys.rates.detail(id),
    queryFn: () => shippingApi.rates.get(id),
    enabled: !!id,
  });
}

export function useCreateRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingRateCreateInput) =>
      shippingApi.rates.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.rates.all });
      toast.success("ສ້າງອັດຕາຄ່າສົ່ງສໍາເລັດ");
    },
  });
}

export function useUpdateRate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingRateUpdateInput) =>
      shippingApi.rates.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.rates.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.rates.all });
      toast.success("ແກ້ໄຂອັດຕາຄ່າສົ່ງສໍາເລັດ");
    },
  });
}

export function useDeleteRate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => shippingApi.rates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.rates.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useShippingSettingsQuery() {
  return useQuery({
    queryKey: shippingKeys.settings,
    queryFn: () => shippingApi.settings.get(),
  });
}

export function useUpdateShippingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShippingSettingsInput) =>
      shippingApi.settings.update(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.settings });
      toast.success("ບັນທຶກຕັ້ງຄ່າສໍາເລັດ");
    },
  });
}
